/**
 * Журнал доступа.
 *
 * Видно не только кто что открыл, но и что фактически применилось:
 * разница между запрошенными и применёнными фильтрами — это след работы
 * прав, и при разборе инцидента нужен именно он.
 */

import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api, ApiError } from '../api'
import type { AccessLogEntry, PermissionAuditEntry } from '../types'
import { Badge, Card, Empty, Loading, Notice, TableWrap, Tabs } from '../components/ui'
import { Segmented } from '../finance/parts'
import { formatDate, formatDateTime } from '../format'

const formatTime = (value: string) => new Date(value).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })

/**
 * Два журнала на одном экране: обращения к отчётам (что смотрели и что
 * применилось) и изменения прав (кто, кому и что поменял).
 */
export function AdminLog() {
  const [params, setParams] = useSearchParams()
  const tab: 'access' | 'changes' = params.get('tab') === 'changes' ? 'changes' : 'access'

  return (
    <div className="stack" style={{ gap: 18 }}>
      <Tabs
        label="Журналы"
        value={tab}
        onChange={(code) => setParams(code === 'changes' ? { tab: 'changes' } : {})}
        items={[
          { value: 'access', label: 'Обращения к отчётам' },
          { value: 'changes', label: 'Изменения прав' },
        ]}
      />
      <div className="tab-panel" key={tab}>
        {tab === 'access' ? <AccessLog /> : <ChangesLog />}
      </div>
    </div>
  )
}

function ChangesLog() {
  const [entries, setEntries] = useState<PermissionAuditEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .permissionAudit(300)
      .then((response) => setEntries(response.entries))
      .catch((caught) => setError(caught instanceof ApiError ? caught.message : 'Не удалось загрузить журнал изменений'))
  }, [])

  if (error) return <Notice tone="bad">{error}</Notice>
  if (!entries) return <Loading rows={6} />

  return (
    <Card flush>
      {entries.length === 0 ? (
        <Empty title="Изменений прав пока не было" hint="Сюда попадает каждое изменение: роли, доступ к отчётам, скрытые показатели и разделы, срез, блокировки." />
      ) : (
        <div className="access-list" style={{ padding: '0 16px' }}>
          {entries.map((entry) => (
            <div className="access-row" key={entry.id}>
              <div className="access-row__main">
                <div className="access-row__title" style={{ fontWeight: 400 }}>
                  {entry.subject_label && (
                    <>
                      {entry.subject_type === 'user' && entry.subject_id ? (
                        <Link to={`/admin/users/${entry.subject_id}`} className="strong">
                          {entry.subject_label}
                        </Link>
                      ) : (
                        <span className="strong">{entry.subject_label}</span>
                      )}
                      {': '}
                    </>
                  )}
                  {entry.summary}
                </div>
                <div className="access-row__note">
                  {formatDateTime(entry.at)} · {entry.actor_name ?? 'система'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

function AccessLog() {
  const [entries, setEntries] = useState<AccessLogEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [onlyDenied, setOnlyDenied] = useState(false)
  const [person, setPerson] = useState('')
  const [report, setReport] = useState('')
  const [titles, setTitles] = useState<Record<string, string>>({})

  useEffect(() => {
    api
      .accessLog(300)
      .then((response) => setEntries(response.entries))
      .catch((caught) => setError(caught instanceof ApiError ? caught.message : 'Не удалось загрузить журнал'))
    // Наименования измерений: в журнале «Филиал: 1», а не «branch: 1».
    api
      .scopeDimensions()
      .then((response) => setTitles(Object.fromEntries(response.dimensions.map((item) => [item.code, item.title]))))
      .catch(() => undefined)
  }, [])

  const describeFilters = (filters: Record<string, string[]>): string => {
    const list = Object.entries(filters ?? {}).filter(([, values]) => values.length > 0)
    if (list.length === 0) return 'без фильтров'
    return list.map(([key, values]) => `${titles[key] ?? key}: ${values.length}`).join(', ')
  }

  if (error) return <Notice tone="bad">{error}</Notice>
  if (!entries) return <Loading rows={6} />

  const people = [...new Set(entries.map((entry) => entry.user_name ?? '—'))].sort((a, b) => a.localeCompare(b, 'ru'))
  const reports = [...new Map(entries.map((entry) => [entry.report_code, entry.report_title] as const)).entries()].sort((a, b) => a[1].localeCompare(b[1], 'ru'))
  const filtered = entries.filter((entry) => (!person || (entry.user_name ?? '—') === person) && (!report || entry.report_code === report))
  const visible = onlyDenied ? filtered.filter((entry) => entry.outcome === 'denied') : filtered
  const deniedCount = filtered.filter((entry) => entry.outcome === 'denied').length
  // Группировка по дням: заголовок дня — отдельная строка таблицы.
  const days: Array<{ day: string; items: AccessLogEntry[] }> = []
  for (const entry of visible) {
    const day = formatDate(entry.at)
    const last = days[days.length - 1]
    if (last && last.day === day) last.items.push(entry)
    else days.push({ day, items: [entry] })
  }

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="row">
        <Segmented
          label="Какие обращения"
          value={onlyDenied ? 'denied' : 'all'}
          onChange={(value) => setOnlyDenied(value === 'denied')}
          options={[
            { value: 'all', label: `Все · ${filtered.length}` },
            { value: 'denied', label: `Отказы · ${deniedCount}` },
          ]}
        />
        <select className="select select--sm" value={person} onChange={(event) => setPerson(event.target.value)} aria-label="Кто">
          <option value="">Все люди</option>
          {people.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <select className="select select--sm" value={report} onChange={(event) => setReport(event.target.value)} aria-label="Отчёт">
          <option value="">Все отчёты</option>
          {reports.map(([code, title]) => (
            <option key={code} value={code}>
              {title}
            </option>
          ))}
        </select>
        {(person || report) && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setPerson(''); setReport('') }}>
            Сбросить
          </button>
        )}
      </div>

      <Card flush>
        {visible.length === 0 ? (
          <Empty title={onlyDenied ? 'Отказов не было' : 'Журнал пуст'} hint="Записи появляются при каждом обращении к отчёту." />
        ) : (
          <TableWrap cards>
            <table className="data">
              <thead>
                <tr>
                  <th>Когда</th>
                  <th>Кто</th>
                  <th>Отчёт</th>
                  <th>Итог</th>
                  <th>Запрошено</th>
                  <th>Применено</th>
                  <th>Сужено правами</th>
                  <th className="num">Строк</th>
                  <th className="num">мс</th>
                </tr>
              </thead>
              <tbody>
                {days.flatMap(({ day, items }) => [
                  <tr key={`day-${day}`} className="row--group">
                    <td colSpan={9}>
                      {day} · {items.length} {items.length % 10 === 1 && items.length % 100 !== 11 ? 'обращение' : items.length % 10 >= 2 && items.length % 10 <= 4 && (items.length % 100 < 10 || items.length % 100 >= 20) ? 'обращения' : 'обращений'}
                    </td>
                  </tr>,
                  ...items.map((entry) => (
                  <tr key={entry.id}>
                    <td className="secondary small">{formatTime(entry.at)}</td>
                    <td>
                      {entry.user_name ?? '—'}
                      {entry.acting_as_user_id && (
                        <div className="tiny">
                          <Badge tone="accent">предпросмотр</Badge>
                        </div>
                      )}
                    </td>
                    <td>{entry.report_title}</td>
                    <td>
                      {entry.outcome === 'allowed' ? (
                        <span className="secondary">выдан</span>
                      ) : (
                        <Badge tone={entry.outcome === 'denied' ? 'bad' : 'warn'}>{entry.outcome === 'denied' ? 'отказ' : 'ошибка'}</Badge>
                      )}
                    </td>
                    <td className="small secondary minor">
                      {describeFilters(entry.requested_filters)}
                      {entry.requested_period && <div className="tiny">{describePeriod(entry.requested_period)}</div>}
                    </td>
                    <td className="small secondary">
                      {describeFilters(entry.applied_filters)}
                      {entry.applied_period && (entry.applied_period.from || entry.applied_period.to) && (
                        <div className="tiny">
                          {describePeriod(entry.applied_period)}
                          {entry.applied_period.narrowed && (
                            <>
                              {' '}
                              <Badge tone="warn">урезан правами</Badge>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="small">
                      {Object.keys(entry.scope_applied).length > 0 ? (
                        <Badge tone="warn">{Object.keys(entry.scope_applied).map((key) => titles[key] ?? key).join(', ')}</Badge>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="num minor">{entry.row_count ?? '—'}</td>
                    <td className="num secondary minor">{entry.duration_ms ?? '—'}</td>
                  </tr>
                  )),
                ])}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>
    </div>
  )
}

/** Период обращения: что просили и что применили, если права его урезали. */
function describePeriod(period: { from: string | null; to: string | null }): string {
  if (period.from && period.to) return `период ${formatDate(period.from)} — ${formatDate(period.to)}`
  if (period.from) return `период с ${formatDate(period.from)}`
  if (period.to) return `период по ${formatDate(period.to)}`
  return 'период не задан'
}
